import { useEffect } from 'react'
import toast from 'react-hot-toast'
import { useRecit, useAdvanceRecit, type StoryRisk } from '@/api/hooks'
import { ApiError } from '@/api/client'
import { SeaScene } from '@/components/game/SeaScene'
import { SkullIcon } from '@/components/game/GameIcons'
import { ResourceTally } from '@/components/game/ui'
import { useCountdown } from '@/hooks/useCountdown'

/** Échelle 1-3 têtes de mort + libellé accessible par niveau de risque. */
const RISK_META: Record<StoryRisk, { skulls: number; label: string }> = {
  low: { skulls: 1, label: 'Risque faible' },
  medium: { skulls: 2, label: 'Risque modéré' },
  high: { skulls: 3, label: 'Risque élevé' },
}

/**
 * Pictogramme de risque d'un choix — 1 à 3 têtes de mort teintées danger.
 * Les effets restent secrets : seul le NIVEAU de risque autorisé par l'auteur
 * est montré (choix à jets pouvant couler une coque / perdre des ressources).
 */
function RiskBadge({ risk }: { risk: StoryRisk }) {
  const meta = RISK_META[risk]
  return (
    <span
      role="img"
      aria-label={meta.label}
      title={meta.label}
      className="inline-flex items-center gap-0.5 shrink-0 text-red-400"
    >
      {Array.from({ length: meta.skulls }, (_, i) => (
        <SkullIcon key={i} aria-hidden="true" className="w-3.5 h-3.5" />
      ))}
    </span>
  )
}

/**
 * Full-screen immersive récit reader — Design B "Brume océane".
 * Hides the HUD via z-50 overlay. Crossing ships behind a translucent serif
 * card, fog-parting launch animation + compass-rose watermark.
 * HARD RULE: choice buttons expose ONLY label + lock hint — never costs/effects.
 */
export function StoryReader({ id, onClose }: { id: number; onClose: () => void }) {
  const { data: view } = useRecit(id)
  const advance = useAdvanceRecit()
  // ETA while the fleet is still sailing out (the journal opens on arrival).
  const eta = useCountdown(view?.arrivesAt ?? null)
  // Re-keying the fog + reader card on the current node replays the entrance
  // animation (fog parting + rise) between each section of the récit.
  const animKey = view?.nodeId ?? 'loading'

  // Esc + scroll-lock (copied from FullscreenContainer)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [onClose])

  function onChoose(choiceIndex: number) {
    advance.mutate(
      { id, choiceIndex },
      {
        onError: (e) => {
          if (e instanceof ApiError) {
            if (e.message.includes('choice_locked') || e.status === 409 && e.message.includes('locked')) {
              toast.error('Tu ne peux pas faire ce choix.')
              return
            }
            if (e.message.includes('choice_unaffordable') || e.status === 409 && e.message.includes('unaffordable')) {
              toast.error('Pas assez de ressources.')
              return
            }
            toast.error(e.message || 'Action impossible.')
          } else {
            toast.error('Action impossible.')
          }
        },
      },
    )
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Récit"
      className="fixed inset-0 z-50 overflow-hidden"
      style={{
        background:
          'radial-gradient(130% 100% at 50% 12%, #103247 0%, #0a2334 42%, #05121c 78%, #03090f 100%)',
      }}
    >
      <StoryReaderStyles />

      {/* Compass-rose watermark — faint, centered, plays recitCompass on mount */}
      <div
        className="recit-compass"
        style={{
          position: 'absolute',
          inset: 0,
          display: 'grid',
          placeItems: 'center',
          pointerEvents: 'none',
        }}
      >
        <svg
          width="min(80vmin,620px)"
          height="min(80vmin,620px)"
          viewBox="0 0 200 200"
          fill="none"
          stroke="#d8c08a"
          strokeWidth={1.5}
        >
          <circle cx="100" cy="100" r="92" />
          <circle cx="100" cy="100" r="70" />
          <path
            d="M100 12l10 78 78 10-78 10-10 78-10-78-78-10 78-10z"
            fill="#d8c08a"
            fillOpacity=".5"
            stroke="none"
          />
        </svg>
      </div>

      <SeaScene scene="map" />

      {/* Fog overlay that parts on mount AND on every node change (re-keyed) */}
      <div
        key={animKey}
        aria-hidden
        className="recit-fog-wrap"
        style={{
          position: 'absolute',
          inset: '-10% -30%',
          zIndex: 3,
          pointerEvents: 'none',
        }}
      >
        <div
          className="recit-fog"
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: '70%',
            background:
              'radial-gradient(circle, rgba(180,205,225,.5), rgba(180,205,225,0) 70%)',
            filter: 'blur(14px)',
            animation: 'recitFogL 1.5s ease-in forwards',
          }}
        />
        <div
          className="recit-fog"
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 0,
            width: '70%',
            background:
              'radial-gradient(circle, rgba(180,205,225,.5), rgba(180,205,225,0) 70%)',
            filter: 'blur(14px)',
            animation: 'recitFogR 1.5s ease-in forwards',
          }}
        />
      </div>

      {/* Close button — top-left, z-30 */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Fermer le récit"
        className="absolute top-3 left-3 z-30 grid place-items-center w-9 h-9 rounded-lg text-sand-200 hover:text-sand-100"
        style={{
          background: 'rgba(6,28,36,.85)',
          border: '1px solid rgba(201,165,92,.3)',
        }}
      >
        ✕
      </button>

      {/* Reader card — top-aligned on mobile (a short card shouldn't float in the
          middle of a tall phone screen), centered on sm+. Scrolls if a node is tall. */}
      <div className="relative z-[2] h-full overflow-y-auto flex items-start sm:items-center justify-center px-4 pt-16 pb-10 sm:p-6">
        <div
          key={animKey}
          className="recit-rise w-full max-w-[720px] rounded-2xl p-6 sm:p-9"
          style={{
            fontFamily: '"Iowan Old Style", Georgia, serif',
            border: '1px solid rgba(150,180,205,.14)',
            background: 'linear-gradient(180deg, rgba(15,38,55,.55), rgba(8,22,33,.5))',
            backdropFilter: 'blur(3px)',
          }}
        >
          {!view && (
            <div className="text-center text-[#9fb0bf] py-8">Chargement…</div>
          )}

          {view && view.state === 'traveling' && (
            <div className="text-center py-6">
              <p
                className="text-xs font-semibold tracking-[0.18em] uppercase mb-2"
                style={{ color: '#d8c08a' }}
              >
                {view.islandName}
              </p>
              <h2 className="text-xl sm:text-2xl font-bold text-[#e4eef4] mb-4">{view.title}</h2>
              <p className="text-[#c8dce8] leading-relaxed mb-3">{view.text}</p>
              <p className="text-[#9fb0bf]">
                Arrivée dans <span className="tabular-nums text-[#e4eef4]">{eta}</span>
              </p>
            </div>
          )}

          {view && view.state !== 'traveling' && !view.isEnding && (
            <>
              {/* Kicker — island name */}
              <p
                className="text-xs font-semibold tracking-[0.18em] uppercase mb-2"
                style={{ color: '#d8c08a' }}
              >
                {view.islandName}
              </p>

              {/* Title */}
              <h2 className="text-xl sm:text-2xl font-bold text-[#e4eef4] mb-4">
                {view.title}
              </h2>

              {/* Node text — the whole card already re-rises per node (keyed). */}
              <p className="text-[#c8dce8] leading-relaxed mb-2">
                {view.text}
              </p>

              {/* Choices — label + lock + risk hint ONLY, no effects/costs */}
              <ul className="space-y-3 mt-6">
                {view.choices.map((c) => (
                  <li key={c.index}>
                    <button
                      type="button"
                      disabled={!c.playable || advance.isPending}
                      onClick={() => onChoose(c.index)}
                      className="w-full text-left rounded-xl px-4 py-3 transition disabled:cursor-not-allowed"
                      style={{
                        border: '1px solid rgba(150,180,205,.18)',
                        borderLeft: c.risk ? '3px solid #f87171' : '3px solid #6fb4e6',
                        background: 'rgba(12,30,44,.55)',
                        opacity: c.playable ? 1 : 0.5,
                      }}
                    >
                      <span className="flex items-start justify-between gap-2">
                        <span className="text-[#e4eef4]">{c.label}</span>
                        {c.risk && <RiskBadge risk={c.risk} />}
                      </span>
                      {!c.playable && c.lock && (
                        <span className="block mt-1 text-xs text-[#9fb0bf]">
                          🔒 {c.lock}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          {view && view.isEnding && (
            <>
              {/* Ending label — large */}
              <p
                className="text-xs font-semibold tracking-[0.18em] uppercase mb-2"
                style={{ color: '#d8c08a' }}
              >
                {view.islandName}
              </p>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#e4eef4] mb-4">
                {view.endingLabel}
              </h2>

              {/* Ending lore text */}
              <p className="text-[#c8dce8] leading-relaxed mb-6">{view.text}</p>

              {/* Reward — icons only, shown after choice resolved */}
              {view.reward && (
                <div className="mb-6">
                  <ResourceTally
                    gold={view.reward.gold || undefined}
                    wood={view.reward.wood || undefined}
                    rhum={view.reward.rhum || undefined}
                  />
                </div>
              )}

              <button
                type="button"
                onClick={onClose}
                className="rounded-xl px-6 py-3 font-semibold text-[#e4eef4] transition"
                style={{
                  background: 'rgba(111,180,230,.18)',
                  border: '1px solid rgba(111,180,230,.35)',
                }}
              >
                Fermer
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Scoped keyframes + reduced-motion overrides for the récit reader.
 * Rendered unconditionally on every mount (same pattern as SeaSceneStyles) —
 * a module-level inject-once guard breaks under StrictMode / route changes.
 */
function StoryReaderStyles() {
  return (
    <style>{`
      @keyframes recitFogL { to { transform: translateX(-60%) } }
      @keyframes recitFogR { to { transform: translateX(60%) } }
      @keyframes recitRise { from { opacity:0; transform: translateY(28px) scale(.985) } to { opacity:1; transform:none } }
      @keyframes recitCompass { 0%{opacity:0;transform:scale(1.05) rotate(-4deg)} 35%{opacity:.34} 100%{opacity:.11;transform:none} }
      .recit-rise { animation: recitRise .55s ease-out both }
      .recit-compass { animation: recitCompass 2.2s ease-out forwards }
      @media (prefers-reduced-motion: reduce){
        .recit-fog, .recit-compass, .recit-rise { animation:none!important }
        .recit-compass { opacity:.11 }
      }
    `}</style>
  )
}
